import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { UserPlus, FileCheck, BadgeCheck, Briefcase, Calendar, Wallet, TrendingUp, Shield, Clock, Star, ArrowRight, Sparkles, Users, Zap } from "lucide-react";

const STEPS = [
  { n: "1", icon: UserPlus, title: "Créez votre compte pro", desc: "Inscription rapide en sélectionnant le rôle professionnel. Renseignez vos informations de base." },
  { n: "2", icon: Briefcase, title: "Complétez votre profil", desc: "Photo, bio, expérience, diplômes, tarif horaire, zone d'intervention et types de garde proposés." },
  { n: "3", icon: FileCheck, title: "Vérification d'identité", desc: "Envoyez votre pièce d'identité (recto/verso) et un selfie. Notre équipe valide sous 24-48h." },
  { n: "4", icon: BadgeCheck, title: "Validation par l'admin", desc: "Une fois votre dossier approuvé, vous recevez la notification d'activation de votre profil." },
  { n: "5", icon: Wallet, title: "Souscrivez à l'abonnement", desc: "L'abonnement pro débloque l'accès aux demandes de garde et à la messagerie avec les parents." },
  { n: "6", icon: Calendar, title: "Recevez vos premières demandes", desc: "Configurez vos disponibilités et acceptez les réservations qui correspondent à votre planning." },
];

const FAQS = [
  { q: "Pourquoi un abonnement pour les pros ?", a: "L'abonnement est la seule source de revenus de Click&Gard. Il finance la modération, le service client, la vérification des profils et la visibilité de votre annonce. Les parents utilisent la plateforme gratuitement et vous règlent ensuite directement la prestation." },
  { q: "Combien puis-je gagner sur Click&Gard ?", a: "Aucune commission, jamais. Vous fixez librement votre tarif horaire et conservez 100% de ce que vous facturez au parent. Click&Gard ne se rémunère que via votre abonnement pro mensuel." },
  { q: "Comment sont versés mes paiements ?", a: "Click&Gard ne gère pas les paiements de garde. Le parent vous règle directement, selon les modalités convenues entre vous (espèces, CESU, virement, PayPal…). Vous gardez 100% des montants perçus." },
  { q: "Combien de temps prend la validation de mon profil ?", a: "La vérification d'identité par notre équipe prend généralement 24 à 48h ouvrées. Vous êtes notifié(e) par email dès que votre profil est activé." },
  { q: "Puis-je gérer mes disponibilités librement ?", a: "Oui, vous définissez vos créneaux disponibles (matin, après-midi, soir, nuit), vos jours d'activité et votre rayon d'intervention. Vous restez maître de votre planning." },
  { q: "Que se passe-t-il en cas de litige ?", a: "Notre service de médiation intervient pour analyser la situation des deux parties et proposer une résolution équitable. Vous n'êtes jamais seul face à un conflit." },
];

const CommentCaMarchePros = () => {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-immersive">
      <Seo
        title="Comment ça marche pour les baby-sitters"
        description="Devenez baby-sitter sur Click&Gard : inscription, vérification d'identité, abonnement pro, gestion du planning et paiements sécurisés. Toutes les étapes expliquées."
        path="/comment-ca-marche-pros"
        jsonLd={faqJsonLd}
      />
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden py-20 md:py-24 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/10 via-transparent to-accent/10" />
        <div className="absolute top-20 right-10 w-72 h-72 bg-secondary/20 rounded-full blur-3xl animate-pulse" />
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 border border-secondary/20 mb-6">
            <Briefcase className="w-4 h-4 text-secondary" />
            <span className="text-sm font-semibold text-secondary">Espace professionnels</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-display font-bold mb-6 bg-gradient-to-r from-secondary via-primary to-accent bg-clip-text text-transparent">
            Devenez baby-sitter sur Click&Gard
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Développez votre activité de garde d'enfants en toute autonomie. Profil vérifié, planning flexible, paiements sécurisés.
          </p>
          <Link to="/auth?tab=register">
            <Button size="lg" variant="neon" className="text-base font-semibold">
              <UserPlus className="w-5 h-5" />
              Devenir baby-sitter
            </Button>
          </Link>
        </div>
      </section>

      {/* ETAPES */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-4">
            Votre parcours en 6 étapes
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            De l'inscription à votre première garde, voici comment lancer votre activité.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {STEPS.map((step) => (
              <article key={step.n} className="relative rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm p-6 hover:border-secondary/40 hover:-translate-y-1 transition-all">
                <div className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-gradient-to-br from-secondary to-primary text-primary-foreground font-bold flex items-center justify-center shadow-lg">
                  {step.n}
                </div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-secondary/20 to-primary/20 flex items-center justify-center mb-4">
                  <step.icon className="w-6 h-6 text-secondary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* AVANTAGES */}
      <section className="py-16 px-4 bg-card/30 backdrop-blur-sm border-y border-border/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12">
            Pourquoi rejoindre Click&Gard ?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: TrendingUp, title: "Développez votre activité", desc: "Accédez à un flux régulier de demandes de garde dans votre zone." },
              { icon: Calendar, title: "Planning flexible", desc: "Vous décidez de vos disponibilités et acceptez les missions qui vous conviennent." },
              { icon: Wallet, title: "0% commission", desc: "Les parents vous règlent directement. Vous conservez 100% de votre tarif sur chaque garde." },
              { icon: Shield, title: "Profil protégé", desc: "Identité vérifiée, avis modérés, médiation en cas de litige." },
              { icon: Users, title: "Communauté de parents", desc: "Construisez votre réputation grâce aux avis vérifiés des familles." },
              { icon: Zap, title: "Outils tout-en-un", desc: "Messagerie, planning, gestion des gains et notifications en temps réel." },
            ].map((a) => (
              <div key={a.title} className="rounded-2xl border border-border/50 bg-card/50 p-6">
                <a.icon className="w-8 h-8 text-secondary mb-3" />
                <h3 className="font-semibold mb-2">{a.title}</h3>
                <p className="text-sm text-muted-foreground">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS VERIF */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <FileCheck className="w-12 h-12 text-secondary mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">Validation en 3 étapes</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Notre processus de vérification protège votre réputation et rassure les parents.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { step: "Email", desc: "Confirmation de votre adresse email à l'inscription." },
              { step: "Identité", desc: "Pièce d'identité officielle + selfie validés par notre équipe." },
              { step: "Abonnement", desc: "Souscription à l'abonnement pro pour activer la réception des demandes." },
            ].map((s, i) => (
              <div key={s.step} className="rounded-xl border border-border/50 bg-card/50 p-6 text-center">
                <div className="w-10 h-10 rounded-full bg-secondary/20 text-secondary font-bold flex items-center justify-center mx-auto mb-3">
                  {i + 1}
                </div>
                <h3 className="font-semibold mb-2">{s.step}</h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-card/30 backdrop-blur-sm border-y border-border/50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12">Questions fréquentes</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="rounded-xl border border-border/50 bg-card/50 backdrop-blur-sm px-5 border-b">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center relative overflow-hidden rounded-3xl border border-secondary/20 bg-gradient-to-br from-secondary/10 via-primary/10 to-accent/10 p-12 backdrop-blur-sm">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-secondary/20 rounded-full blur-3xl" />
          <div className="relative">
            <Sparkles className="w-10 h-10 text-secondary mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">Prêt à lancer votre activité ?</h2>
            <p className="text-lg text-muted-foreground mb-8">Rejoignez les baby-sitters Click&Gard en quelques minutes.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/auth?tab=register">
                <Button size="lg" variant="neon">Créer un compte pro</Button>
              </Link>
              <Link to="/subscription">
                <Button size="lg" variant="outline">
                  Voir l'abonnement
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CommentCaMarchePros;
